import { type PermitRow, type TagCount, customerTags } from '@mollusk/shared';

/** 'tenant_improvement' → 'Tenant improvement'. */
export function scopeLabel(scope: string | null | undefined): string {
  if (!scope) return '—';
  const words = scope.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The readable type ("Mechanical Permit"), falling back to the source code. */
export function typeLabel(p: PermitRow): string {
  return p.permit_type_display ?? p.permit_type ?? '—';
}

/** Canonical date (issued → applied → final → status), as YYYY-MM-DD or null. */
export const permitDate = (p: PermitRow): string | null =>
  p.permit_date ?? p.issued_date ?? p.applied_date ?? p.final_date ?? p.status_date ?? null;

/** True when `permitDate` is the status date: the permit has no other date. */
export const isStatusDated = (p: PermitRow): boolean =>
  !!p.status_date &&
  !(p.issued_date ?? p.applied_date ?? p.final_date) &&
  (!p.permit_date || !!p.permit_date_source?.startsWith('status'));

export const STATUS_DATE_LABEL = 'Status date';
export const STATUS_DATE_HELP = 'Last update in the source system, not an issue date';

export interface PermitFilters {
  tag?: string | undefined;
  type?: string | undefined;
  year?: string | undefined;
}

export function permitFacets(rows: readonly PermitRow[]) {
  const tags = new Set<string>();
  const types = new Set<string>();
  const years = new Set<string>();
  for (const r of rows) {
    for (const t of customerTags(r.tags)) tags.add(t);
    const t = typeLabel(r);
    if (t !== '—') types.add(t);
    const d = permitDate(r);
    if (d) years.add(d.slice(0, 4));
  }
  return {
    tags: [...tags].sort(),
    types: [...types].sort(),
    years: [...years].sort().reverse(),
  };
}

export function filterPermits(rows: readonly PermitRow[], f: PermitFilters): PermitRow[] {
  return rows.filter(
    (r) =>
      (!f.tag || (r.tags ?? []).includes(f.tag)) &&
      (!f.type || typeLabel(r) === f.type) &&
      (!f.year || permitDate(r)?.startsWith(f.year)),
  );
}

export type PermitSortKey = 'date' | 'number' | 'type' | 'description' | 'scope' | 'final';

const sortValue = (r: PermitRow, key: PermitSortKey): string | null => {
  switch (key) {
    case 'date':
      return permitDate(r);
    case 'number':
      return r.source_permit_number;
    case 'type': {
      const t = typeLabel(r);
      return t === '—' ? null : t;
    }
    case 'description':
      return r.description;
    case 'scope':
      return r.permit_scope;
    case 'final':
      return r.final_date;
  }
};

export function sortPermits(
  rows: readonly PermitRow[],
  key: PermitSortKey,
  dir: 'asc' | 'desc',
): PermitRow[] {
  const sign = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    if (av == null || bv == null) return av == null ? (bv == null ? 0 : 1) : -1;
    return av.localeCompare(bv) * sign;
  });
}

/** The "Address" scope of the Counts box, from the permits already loaded. */
export function addressTagCounts(
  rows: readonly PermitRow[],
  top = 6,
): {
  total: number;
  top_tags: TagCount[];
} {
  const counts = new Map<string, number>();
  for (const r of rows)
    for (const t of customerTags(r.tags)) counts.set(t, (counts.get(t) ?? 0) + 1);
  return {
    total: rows.length,
    top_tags: [...counts]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, top)
      .map(([tag, count]) => ({ tag, count })),
  };
}

/** Enter in the address box searches the top suggestion. */
export function pickOnEnter<T>(suggestions: readonly T[]): T | null {
  return suggestions[0] ?? null;
}
